import { Box, Stack, useTheme } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import ControlBtn from '../carousel-embla/components/control-btn';
import { useIsMobile } from '../../hooks';
import { chevronLeftIcon, chevronRightIcon } from '../../config/icons';
import { Thumbnails } from './thumbnails';
import { useEffect, useState, useCallback } from 'react';
import Slide from './slide';

type CarouselContentProps = {
  urls: string[];
  mainImageUrl?: string;
  startIndex?: number;
};

export default function CarouselContentFullscreen({
  urls,
  mainImageUrl,
  startIndex = 0,
}: CarouselContentProps) {
  const isMobile = useIsMobile();
  const direction = useTheme().direction;
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
      containScroll: false,
      direction,
      startIndex,
    },
    [],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.reInit();
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <>
      <Box
        ref={emblaRef}
        sx={{ width: '100%', height: '80%', flexGrow: 1, marginY: 2, position: 'relative' }}
      >
        <Stack direction="row" height="100%">
          {urls.map((url, index) => (
            <Slide
              key={index}
              url={url}
              isActive={index === currentIndex}
              style={{
                marginLeft: '16px',
                marginRight: '16px',
              }}
            />
          ))}
        </Stack>
        {!isMobile && urls.length > 1 && (
          <>
            <ControlBtn
              onClick={() => emblaApi?.scrollPrev()}
              top="48%"
              left="20px"
              icon={direction === 'rtl' ? chevronRightIcon : chevronLeftIcon}
            />
            <ControlBtn
              onClick={() => emblaApi?.scrollNext()}
              top="48%"
              right="15px"
              icon={direction === 'rtl' ? chevronLeftIcon : chevronRightIcon}
            />
          </>
        )}
      </Box>
      <Stack spacing={1} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', py: 4, width: '100%' }}>
        <Thumbnails urls={urls} mainImageUrl={mainImageUrl} emblaMainApi={emblaApi} />
      </Stack>
    </>
  );
}
